import Head from 'next/head';
import { Event } from '../lib/types';
import { getEventImage, getEventVideoUrl, getEventPosterUrl } from '../lib/utils';

interface SocialMetaTagsProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  event?: Event;
  type?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
}

export default function SocialMetaTags({
  title,
  description,
  image,
  url,
  event,
  type = 'website',
  twitterCard = 'summary_large_image'
}: SocialMetaTagsProps) {
  // Use event image if available, otherwise fallback to provided image or default
  const socialImage = event ? getEventPosterUrl(event) || getEventImage(event) : image;
  const finalImage = socialImage || '/android-icon-192x192.png'; // Fallback to app icon
  
  // Ensure absolute URL for image
  const absoluteImage = finalImage.startsWith('http') 
    ? finalImage 
    : `${process.env.NEXT_PUBLIC_BASE_URL || 'https://invitd.us'}${finalImage}`;

  // Ensure absolute URL for page
  const absoluteUrl = url?.startsWith('http') 
    ? url 
    : `${process.env.NEXT_PUBLIC_BASE_URL || 'https://invitd.us'}${url || ''}`;

  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={event ? `${event.event_name} event image` : 'Invitd event'} />
      {absoluteUrl && <meta property="og:url" content={absoluteUrl} />}
      <meta property="og:site_name" content="Invitd" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content="@invitdus" />
      <meta name="twitter:creator" content="@invitdus" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={event ? `${event.event_name} event image` : 'Invitd event'} />
      {/* Video tags for Open Graph if available */}
      {event && getEventVideoUrl(event) && (
        <>
          <meta property="og:video" content={getEventVideoUrl(event)} />
          <meta property="og:video:type" content="video/mp4" />
        </>
      )}
      
      {/* LinkedIn */}
      <meta property="og:image:secure_url" content={absoluteImage} />
      
      {/* WhatsApp & Telegram (uses Open Graph) */}
      <meta property="og:image:type" content="image/jpeg" />
      
      {/* iMessage & RCS (Apple Messages) */}
      <meta name="apple-mobile-web-app-title" content="Invitd" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* Additional Social Platforms */}
      <meta property="al:ios:app_store_id" content="YOUR_IOS_APP_ID" />
      <meta property="al:ios:app_name" content="Invitd" />
      <meta property="al:android:package" content="YOUR_ANDROID_PACKAGE" />
      <meta property="al:android:app_name" content="Invitd" />
      
      {/* Rich Link Preview optimizations */}
      <meta name="theme-color" content="#3b82f6" />
      <meta name="msapplication-TileColor" content="#3b82f6" />
      
      {/* Additional meta for better sharing */}
      {event && (
        <>
          <meta property="event:start_time" content={new Date(event.event_date).toISOString()} />
          {event.event_date_end && (
            <meta property="event:end_time" content={new Date(event.event_date_end).toISOString()} />
          )}
          {(event.event_location_friendly || event.event_address) && (
            <meta property="place:location:locality" content={event.event_location_friendly || event.event_address} />
          )}
        </>
      )}
      
      {/* JSON-LD Structured Data for Events */}
      {event && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Event",
              "name": event.event_name,
              "description": description,
              "image": absoluteImage,
              "startDate": new Date(event.event_date).toISOString(),
              ...(event.event_date_end && { "endDate": new Date(event.event_date_end).toISOString() }),
              "organizer": {
                "@type": "Organization",
                "name": event.hosted_by || "Event Host",
                "url": "https://invitd.us"
              },
              ...((event.event_location_friendly || event.event_address) && {
                "location": {
                  "@type": "Place",
                  "name": event.event_location_friendly || event.event_address,
                  "address": event.event_address || event.event_location_friendly
                }
              }),
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock"
              }
            })
          }}
        />
      )}
    </Head>
  );
}
